import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './NotificationBell.css';

export interface AppNotification {
  id: string;
  title: string;
  detail: string;
  to: string;
  /** Stored notifications only: false once the user has read it (it stays listed but is dimmed and not counted). */
  unread?: boolean;
  /** Stored notifications only: "2 mins ago". */
  time?: string;
  /** Stored notifications only: marks this one read when the user opens it. */
  onOpen?: (() => void) | undefined;
}

/**
 * The top-bar notification bell, beside the account menu. Each notification is derived from live state (for example the number of employee
 * questions waiting for a reply), so there is no read/unread bookkeeping to drift: it disappears when the work is done. The red number is the
 * total of what is waiting. A user who cannot see a source simply gets no entry for it.
 */
export function NotificationBell({
  notifications,
  count,
  onMarkAllRead,
}: {
  notifications: AppNotification[];
  count: number;
  /** When given, the panel offers "Mark All As Read" (stored notifications only; items derived from live state clear themselves). */
  onMarkAllRead?: (() => void) | undefined;
}) {
  const [open, setOpen] = useState(false);
  const uid = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a route change always closes the panel
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const panelId = `${uid}-panel`;
  const label = count > 0 ? `Notifications, ${count} waiting` : 'Notifications';
  return (
    <div className="notif-bell" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`notif-bell__trigger${count > 0 ? ' notif-bell__trigger--unread' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {count > 0 && <span className="notif-bell__count" aria-hidden="true">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div id={panelId} className="notif-bell__panel" role="region" aria-label="Notifications">
          <div className="notif-bell__header">
            <p className="notif-bell__title">Notifications{count > 0 ? <span className="notif-bell__title-count"> ({count})</span> : null}</p>
            {onMarkAllRead && count > 0 && (
              <button type="button" className="notif-bell__mark-all" onClick={onMarkAllRead}>Mark All As Read</button>
            )}
          </div>
          {notifications.length === 0 ? (
            <p className="notif-bell__empty" role="status">You&rsquo;re all caught up.</p>
          ) : (
            <ul className="notif-bell__list">
              {notifications.map((n) => (
                <li key={n.id}>
                  <Link
                    to={n.to}
                    className={`notif-bell__item${n.unread === false ? ' notif-bell__item--read' : ''}`}
                    onClick={n.onOpen}
                  >
                    <span className="notif-bell__item-title">
                      {n.title}
                      {n.unread === true && <span className="notif-bell__unread-dot" aria-label="Unread" />}
                    </span>
                    <span className="notif-bell__item-detail">{n.detail}</span>
                    {n.time && <span className="notif-bell__item-time">{n.time}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
